import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import DateSchema from "_shared/dates/date-schema";
import { JwtPayload } from "jwt-decode";
import AHDate from "_shared/dates/date";

export type UserAuthTokenJSON = z.infer<typeof UserAuthTokenSchema.UserAuthTokenJSONZ>;
export type UserAuthTokenParams = z.infer<typeof UserAuthTokenSchema.UserAuthTokenZ>;

export default class UserAuthTokenSchema {
  public static readonly UserAuthTokenJSONZ = z.object({
    value: z.string(),
    data: z.custom<JwtPayload>().optional(),
    tokenExp: z.iso.datetime({ offset: true }),
  });

  public static readonly UserAuthTokenZ = z.object({
    value: z.string(),
    data: z.custom<JwtPayload>().optional(),
    tokenExp: z.instanceof(AHDate),
  });

  public static validate(values: unknown): UserAuthTokenParams {
    return parseOrThrow(UserAuthTokenSchema.UserAuthTokenZ, values, {
      logError: true,
      schemaName: "UserAuthToken",
      operation: "validation",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): UserAuthTokenJSON | null | undefined {
    if (isNullOrUndefined(values)) return values;
    return parseOrThrow(UserAuthTokenSchema.UserAuthTokenJSONZ, values, {
      logError: true,
      schemaName: "UserAuthToken",
      operation: "validationJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is UserAuthTokenParams {
    try {
      UserAuthTokenSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is UserAuthTokenJSON {
    try {
      UserAuthTokenSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: unknown): UserAuthTokenParams {
    const parsed = parseOrThrow(UserAuthTokenSchema.UserAuthTokenJSONZ, data, {
      logError: true,
      schemaName: "UserAuthToken",
      operation: "deserialize",
      payloadSample: data,
    });
    return UserAuthTokenSchema.codec.decode(parsed);
  }

  public static serialize(UserAuthToken: UserAuthTokenParams): UserAuthTokenJSON {
    const parsed = UserAuthTokenSchema.validate(UserAuthToken);
    return UserAuthTokenSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(UserAuthTokenSchema.UserAuthTokenJSONZ, UserAuthTokenSchema.UserAuthTokenZ, {
    decode: (data) => ({
      value: data.value,
      data: data.data,
      tokenExp: new AHDate(DateSchema.deserialize(data.tokenExp)),
    }),
    encode: (token) => ({
      value: token.value,
      data: token.data,
      tokenExp: token.tokenExp.toJSON(),
    }),
  });
}
